import {uniq} from 'es-toolkit'
import {toString} from 'es-toolkit/compat'

import * as shippingApi from '../../../services/api/shipping'
import {
  PossiblyMixedValue,
  isMixedValue,
  toIntegerValue,
  toNumberValue,
  toPossiblyMixedValue,
  toStringValue,
} from '../../utils/form'

import {Provider, Shipping} from '../../../types'

import {
  MixedFormData,
  ShippingPrice,
  ShippingPriceType,
  SingleFormData,
  StaggeredWeightPrice,
} from './form'

const toRequestPrice = (price: SingleFormData['price']): shippingApi.ShippingRequest['price'] =>
  Array.isArray(price)
    ? (price as StaggeredWeightPrice[]).map(({weight, price: thisPrice}) => ({
        weight: toNumberValue(weight) as number,
        price: toNumberValue(thisPrice) as number,
      }))
    : toNumberValue(price) || 0

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const formDataTransforms: Record<string, (value: any) => any> = {
  name: name => name,
  price: toRequestPrice,
  deliveryTimeFast: toIntegerValue,
  deliveryTimeSlow: toIntegerValue,
  type: type => type,
  carrierId: carrierId => carrierId,
}

export const formDataToShippingRequests = (
  formData: SingleFormData,
  provider: Provider
): shippingApi.ShippingRequest[] =>
  formData.countryCodes.map(countryCode => ({
    name: formDataTransforms.name(formData.name),
    price: formDataTransforms.price(formData.price),
    deliveryTimeFast: formDataTransforms.deliveryTimeFast(formData.deliveryTimeFast),
    deliveryTimeSlow: formDataTransforms.deliveryTimeSlow(formData.deliveryTimeSlow),
    type: formDataTransforms.type(formData.type),
    carrierId: formDataTransforms.carrierId(formData.carrierId),
    countryCode,
    currency: provider.currency,
    provider: provider.id,
    available: true,
  }))

export const formDataToPartialShippingRequest = (formData: Partial<SingleFormData>) => {
  const priceRequest: Partial<shippingApi.ShippingRequest> = {}
  Object.keys(formData).forEach(key => {
    const value = formData[key as keyof SingleFormData]
    const shippingRequestTransform = formDataTransforms[key]

    if (!isMixedValue(value) && shippingRequestTransform) {
      priceRequest[key as keyof shippingApi.ShippingRequest] = shippingRequestTransform(value)
    }
  })

  return priceRequest
}

const toPrice = (value: Shipping['price']): ShippingPrice =>
  Array.isArray(value)
    ? value.map(({weight, price}) => ({
        weight: toStringValue(weight),
        price: toStringValue(price),
      }))
    : toStringValue(value)

const toPriceType = <T>(
  shippings: Shipping[],
  determineType: (shipping: Shipping) => T
): PossiblyMixedValue<T> => {
  const types = shippings.map(determineType)
  const isMultipleTypes = uniq(types).length > 1
  return isMultipleTypes ? {mixed: true, values: types} : types[0]
}

export const shippingsToFormData = (shippings: Shipping[]): MixedFormData => ({
  name: toPossiblyMixedValue(
    shippings.map(shipping => shipping.name),
    toString
  ),
  price: toPossiblyMixedValue(
    shippings.map(shipping => shipping.price),
    toPrice
  ),
  priceType: toPriceType<ShippingPriceType>(shippings, (shipping: Shipping) =>
    Array.isArray(shipping.price) ? ShippingPriceType.WEIGHT : ShippingPriceType.SINGLE
  ),
  deliveryTimeFast: toPossiblyMixedValue(
    shippings.map(shipping => shipping.deliveryTimeFast),
    toStringValue
  ),
  deliveryTimeSlow: toPossiblyMixedValue(
    shippings.map(shipping => shipping.deliveryTimeSlow),
    toStringValue
  ),
  type: toPossiblyMixedValue(
    shippings.map(shipping => shipping.type),
    type => type
  ),
  carrierId: toPossiblyMixedValue(
    shippings.map(shipping => shipping.carrierId),
    toString
  ),
  countryCodes: [], // Country code is not editable
})
